import { isJsonObject } from './bytes';
import {
  KeyResponseInvalidError,
  KeyServiceRequestError,
  KeyServiceUnavailableError,
  SignInRequiredError,
} from './errors';

import type { KeyServiceDeps } from './types';

export interface KeyRequestBody {
  downloadId: string;
  viewerPublicKey: string;
  launcherVersion: number;
  piiNoticeVersion?: string;
}

export interface HeartbeatRequestBody {
  downloadId: string;
  launcherVersion: number;
  jti: string;
}

export interface TableViewRequestBody {
  viewId: string;
  piiNoticeVersion?: string;
}

export interface TableViewHeartbeatRequestBody {
  viewId: string;
  jti: string;
}

export type SessionHeartbeatPath = '/key/heartbeat' | '/view/heartbeat';

export type SessionHeartbeatBody = HeartbeatRequestBody | TableViewHeartbeatRequestBody;

type KeyServicePath = '/key' | '/view' | SessionHeartbeatPath;

type KeyServiceRequestBody = KeyRequestBody | TableViewRequestBody | SessionHeartbeatBody;

interface DenyBody {
  allow: false;
  reason: string;
}

export function postJson(
  deps: KeyServiceDeps,
  path: KeyServicePath,
  idToken: string,
  body: KeyServiceRequestBody,
  signal?: AbortSignal,
): Promise<Response> {
  const url = keyServiceUrl(deps.config.keyApiBaseUrl, path);
  // Called unbound: a browser's fetch throws "Illegal invocation" when called as deps.fetch().
  const fetchFn = deps.fetch ?? globalThis.fetch;
  return fetchFn(url, {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'omit',
    cache: 'no-store',
    redirect: 'error',
    referrerPolicy: 'no-referrer',
    signal,
  });
}

// The user sees only that the access service is unreachable, so the console is where whoever
// deployed the page learns that the build config is wrong.
function keyServiceUrl(baseUrl: string, path: KeyServicePath): string {
  const url = `${baseUrl.replace(/\/+$/, '')}${path}`;
  if (!isHttpsUrl(url)) {
    console.error(
      'IRM keyApiBaseUrl in the build config is not an https URL: no key request is sent',
    );
    throw new TypeError('keyApiBaseUrl must be an https URL');
  }
  return url;
}

function isHttpsUrl(url: string): boolean {
  try {
    return new URL(url).protocol === 'https:';
  } catch {
    return false;
  }
}

export async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return undefined;
  }
}

export function isDenyBody(value: unknown): value is DenyBody {
  return isJsonObject(value) && value.allow === false && typeof value.reason === 'string';
}

export function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

export function lockGraceSecondsOrZero(lockGraceSeconds: unknown): number {
  return isFiniteNumber(lockGraceSeconds) && lockGraceSeconds > 0 ? lockGraceSeconds : 0;
}

// Only config and getIdToken cross over: the internal fetch and pageEvents seams are for tests,
// and a route that passed them would silently replace the window's resume events.
export function keyServiceDepsOf(
  deps: Pick<KeyServiceDeps, 'config' | 'getIdToken'>,
): KeyServiceDeps {
  return { config: deps.config, getIdToken: () => deps.getIdToken() };
}

export async function idTokenOrSignIn(deps: KeyServiceDeps): Promise<string> {
  try {
    return await deps.getIdToken();
  } catch (cause) {
    throw new SignInRequiredError({ cause });
  }
}

export function keyServiceError(status: number, payload: unknown): Error {
  if (status === 429 || status >= 500) return new KeyServiceUnavailableError(status);
  if (status >= 400) {
    return new KeyServiceRequestError(status, errorCode(payload) ?? `http_${status}`);
  }
  return new KeyServiceUnavailableError(status);
}

function errorCode(value: unknown): string | undefined {
  return isJsonObject(value) && typeof value.error === 'string' ? value.error : undefined;
}

export function requireString(payload: Record<string, unknown>, name: string): string {
  const value = payload[name];
  if (typeof value !== 'string') {
    throw new KeyResponseInvalidError(name);
  }
  return value;
}
